import React, { useState, useEffect } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";
import { useTranslation } from "react-i18next";

export default function AdminCreateDriver() {
  const { t } = useTranslation();

  const [userno, setUserno] = useState("");
  const [fullname, setFullname] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  const [drivers, setDrivers] = useState([]);
  const [message, setMessage] = useState("");
  const [lastAction, setLastAction] = useState("");
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchDrivers();
  }, []);

  const getErrorMessage = (err, fallbackKey) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    return t(fallbackKey);
  };

  const fetchDrivers = async () => {
    setLoadingDrivers(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/drivers`);
      setDrivers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch drivers", err);
      setDrivers([]);
      setMessage("❌ " + t("admin_create_driver.messages.fetch_drivers_failed"));
      setLastAction("fetch");
    } finally {
      setLoadingDrivers(false);
    }
  };

  const resetForm = () => {
    setUserno("");
    setFullname("");
    setPassword("");
    setPhone("");
    setEmail("");
    setNotes("");
  };

  const handleSubmit = async () => {
    if (!userno.trim() || !fullname.trim() || !password.trim()) {
      setMessage("❌ " + t("admin_create_driver.messages.missing_required_fields"));
      setLastAction("submit");
      return;
    }

    if (!/^\d{4}$/.test(password.trim())) {
      setMessage("❌ " + t("admin_create_driver.messages.invalid_password"));
      setLastAction("submit");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/drivers`, {
        userno: userno.trim(),
        fullname: fullname.trim(),
        password: password.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        notes: notes.trim() || null,
      });

      setMessage("✅ " + t("admin_create_driver.messages.success"));
      setLastAction("submit");
      resetForm();
      fetchDrivers();
    } catch (err) {
      console.error("❌ Error creating driver:", err);
      setMessage("❌ " + getErrorMessage(err, "admin_create_driver.messages.failure"));
      setLastAction("submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StandardScreen
      background="default"
      topNav={<TopNav title={t("admin_create_driver.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={{ maxWidth: 720, width: "100%", margin: "0 auto" }}>
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 700 }}>
                  {t("admin_create_driver.title")}
                </h1>

                <FormField label={t("admin_create_driver.fields.userno.label")}>
                  <TextInput
                    value={userno}
                    onChange={(e) => {
                      setUserno(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t("admin_create_driver.fields.userno.placeholder")}
                  />
                </FormField>

                <FormField label={t("admin_create_driver.fields.fullname.label")}>
                  <TextInput
                    value={fullname}
                    onChange={(e) => {
                      setFullname(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t("admin_create_driver.fields.fullname.placeholder")}
                  />
                </FormField>

                <FormField label={t("admin_create_driver.fields.password.label")}>
                  <TextInput
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t("admin_create_driver.fields.password.placeholder")}
                    maxLength={4}
                  />
                </FormField>

                <FormField label={t("admin_create_driver.fields.phone.label")}>
                  <TextInput
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t("admin_create_driver.fields.phone.placeholder")}
                  />
                </FormField>

                <FormField label={t("admin_create_driver.fields.email.label")}>
                  <TextInput
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t("admin_create_driver.fields.email.placeholder")}
                  />
                </FormField>

                <FormField label={t("admin_create_driver.fields.notes.label")}>
                  <TextInput
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t("admin_create_driver.fields.notes.placeholder")}
                  />
                </FormField>

                <Button
                  onClick={() => {
                    setLastAction("submit");
                    handleSubmit();
                  }}
                  disabled={!userno.trim() || !fullname.trim() || !password.trim() || submitting}
                  fullWidth
                >
                  {submitting
                    ? t("admin_create_driver.status.submitting")
                    : lastAction === "submit" && message.startsWith("✅")
                    ? t("admin_create_driver.status.success_short")
                    : lastAction === "submit" && message.startsWith("❌")
                    ? `${t("admin_create_driver.status.error_short")}: ${message.replace("❌ ", "")}`
                    : t("admin_create_driver.actions.submit")}
                </Button>
              </Stack>
            </Card>

            <StatusMessage message={message} />

            <Card>
              <Stack gap="3">
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                  <h2 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700 }}>
                    {t("admin_create_driver.list.title")}
                  </h2>
                  <Button onClick={fetchDrivers} disabled={loadingDrivers}>
                    {loadingDrivers
                      ? t("admin_create_driver.status.loading")
                      : t("admin_create_driver.actions.refresh")}
                  </Button>
                </div>

                {drivers.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>
                    {loadingDrivers
                      ? t("admin_create_driver.list.loading")
                      : t("admin_create_driver.list.empty")}
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_driver.list.columns.fullname")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_driver.list.columns.phone")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_driver.list.columns.email")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_driver.list.columns.status")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {drivers.map((driver) => (
                          <tr key={driver.driver_id}>
                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {driver.fullname || "-"}
                            </td>
                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {driver.phone || "-"}
                            </td>
                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {driver.email || "-"}
                            </td>
                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {driver.status || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}